import type { ReactNode } from 'react';

export type TimeDivision = 'Year' | 'Month' | 'Week' | 'Day' | 'Hour';

export interface ApplicationErrorOptions {
  httpCode?: number;
  error: Error;
  message: string | ReactNode;
  asNotification?: boolean;
  duration?: number;
  link?: string;
  sourceAction?: string;
}

export interface ApplicationError extends ApplicationErrorOptions {
  id: string;
  timestamp: number;
  acknowledgedTimestamp?: number;
}

export interface Address {
  companyName?: string;
  name?: string;
  address1?: string;
  address2?: string;
  address3?: string;
  address4?: string;
  county?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  phoneNumber?: string;
  validatedAddressMetadata?: ValidatedAddressMetadata;
  addressType?: 'commercial' | 'residential';
  countryCode?: string;
}

export interface ValidatedAddressMetadata {
  latitude: number;
  longitude: number;
  timeZoneId: string;
  validated?: boolean;
  daylightSavings?: boolean;
}

export type PartnerSetupStep =
  | 'REGISTER'
  | 'INVITE'
  | 'DATA_UPLOAD'
  | 'NETWORK_CONFIG'
  | 'VOLUME_ESTIMATES'
  | 'REPORTING_CONFIG'
  | 'CARRIER_ACCOUNT'
  | 'LABEL_CONFIGURATION'
  | 'PACKAGE_CONFIGURATION';

export type SetActivePartnerPayload = {
  partnerId: string;
  discardPath: boolean;
};

export const PartnerSetupSteps: Record<PartnerSetupStep, { title: string; description: string }> = {
  REGISTER: {
    title: 'Register',
    description: 'Register your partner organization',
  },
  INVITE: {
    title: 'Invite',
    description: 'Invite Members to your organization',
  },
  DATA_UPLOAD: {
    title: 'Upload your Data',
    description: 'Upload your historical shipping data',
  },
  NETWORK_CONFIG: {
    title: 'Fufillment Network Configuration',
    description: 'Add your FC and configure your network',
  },
  VOLUME_ESTIMATES: {
    title: 'Volume Estimates',
    description: 'Set up your page view volume estimates',
  },
  REPORTING_CONFIG: {
    title: 'Reporting Configuration',
    description: 'Configure reports so that we can start generating them for you ',
  },
  CARRIER_ACCOUNT: {
    title: 'Carrier Account Configuration',
    description: 'Add your carrier accounts and we will select the best method for each shipment',
  },
  LABEL_CONFIGURATION: {
    title: 'Label Configuration',
    description: 'Give us some details about your shipping labels and we will print them',
  },
  PACKAGE_CONFIGURATION: {
    title: 'Package Configuration',
    description:
      'Tell us what packaging options are available at your FCs and we will help you optimally pack your shipments',
  },
};

export interface SetupStep {
  title: string;
  description: string;
  complete: boolean;
  path: string;
}

export type FCStandardHoliday =
  | 'VETERANS'
  | 'CHRISTMAS'
  | 'NYD'
  | 'MLK'
  | 'PRESIDENTS'
  | 'MEMORIAL'
  | 'INDEPENDENCE'
  | 'LABOR'
  | 'COLUMBUS'
  | 'THANKSGIVING';
export const FCHolidayDescriptionMap: Record<FCStandardHoliday, string> = {
  NYD: `New Year's Day`,
  MLK: `Martin Luther King Day`,
  PRESIDENTS: `President's Day`,
  MEMORIAL: `Memorial Day`,
  INDEPENDENCE: `Independence Day`,
  LABOR: `Labor Day`,
  COLUMBUS: `Columbus Day`,
  VETERANS: `Veteran's Day`,
  THANKSGIVING: 'Thanksgiving',
  CHRISTMAS: 'Christmas',
};

export type shipmentSearchLabelFilter = 'labelCreatedOnly' | 'carrierSelectionOnly' | 'all';
export const shipmentSearchLabelFilterOptions: Record<shipmentSearchLabelFilter, string> = {
  labelCreatedOnly: 'ONLY Shipments with Labels',
  carrierSelectionOnly: 'ONLY Carrier Selections without Labels',
  all: 'All Shipments',
};

export type LTLshipmentSearchLabelFilter = 'includeLTL' | 'notLTL';
export const LTLshipmentSearchLabelFilterOptions: Record<LTLshipmentSearchLabelFilter, string> = {
  includeLTL: 'Include LTL Shipments',
  notLTL: 'NON LTL Shipments',
};
